import { useState, useEffect, useRef } from "react";
import arrayUtilities from "../../utilities/arrayUtilities";
import stringHelpers from "../../utilities/stringHelpers";
import Api from "../api/api";
import { ReportsCharts } from "./reportsCharts";
import { dateRange, toDateString, isDateRangeComplete } from "../../utilities/dateUtilities";
import { reportDatePresetByLabel } from "./reportsEnums";
import { saveAs } from 'file-saver';
import { EmptyState } from "../dashboard/emptyState";
import Loader from "../shared/loader";
import { createGroupedChartTraces } from "./chartUtils";
import { Link } from "react-router-dom/cjs/react-router-dom";
import { FiltersModalTrigger } from "../shared/filtersModalTrigger";
import { AppliedFilterPill, renderTagFilterItemLabel } from "../shared/appliedFilterPill";
import appliedFiltersUtilities from "../../utilities/appliedFiltersUtilities";
import { PcnsPaidReportFiltersModal } from "./filters-modals/pcnsPaidReportFiltersModal";
import { PcnsPaidScheduleModal } from "./schedule-modals/pcnsPaidScheduleModal";
import { downloadReportPdf } from "./downloadReportPdf";
import { PrintPdfHeader } from "./printPdfHeader";

const pcnsGroupByOptions = { 0: "Site and Amount", 1: "Site", 2: "Tag", 3: "Tag and Site" }

export const PcnsPaidReport = ({ sites, clients, tags, organisationId, token, user, organisationName, organisationLogo }) => {
    const [reportsRawData, setReportsRawData] = useState(null);
    const [reportsData, setReportsData] = useState(null);
    const [fromDate, setFromDate] = useState(dateRange("lastMonth").firstDate);
    const [toDate, setToDate] = useState(dateRange("lastMonth").lastDate);
    const [datePreset, setDatePreset] = useState(reportDatePresetByLabel["Last month"]);
    const [issueDateFrom, setIssueDateFrom] = useState(null);
    const [issueDateTo, setIssueDateTo] = useState(null);
    const [selectedClient, setSelectedClient] = useState("");
    const [sitesByClient, setSitesByClient] = useState(sites)
    const [selectedSiteByClient, setSelectedSiteByClient] = useState("")
    const [pcnsGroupBy, setPcnsGroupBy] = useState(0)
    const [selectedTags, setSelectedTags] = useState([])
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(null);
    const [pcnsPaidColumnTotal, setPcnsPaidColumnTotal] = useState(null)
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [showFilterPills, setShowFilterPills] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [showScheduleExportModal, setShowScheduleExportModal] = useState(false);
    const zeroRevenueSitesPreferenceKey = `${ user.id }-pcnsPaidShowZeroRevenueSites`
    const [showZeroRevenueSites, setShowZeroRevenueSites] = useState(stringHelpers.zeroRevenueSitesPreference(zeroRevenueSitesPreferenceKey))
    const reportsLandingUrl = `/organisation/${ organisationId }/reports`;
    const pdfRef = useRef()

    const pdfClientName = (clientId) => {
        if (!stringHelpers.isNullOrEmpty(clientId)) {
            const client = clients.find(item => item.id === clientId)
            if (client !== undefined) {
                return client.name
            }
        }
        return "All"
    }

    const pdfSiteName = (siteId) => {
        if (!stringHelpers.isNullOrEmpty(siteId)) {
            const site = sites.find(item => item.id === siteId)
            if (site !== undefined) {
                return site.name
            }
        }
        return "All"
    }

    const pdfGroupByName = (groupBy) => pcnsGroupByOptions[groupBy]

    const handleResetFilters = () => {
        const defaultRange = dateRange("lastMonth");
        setFromDate(defaultRange.firstDate);
        setToDate(defaultRange.lastDate);
        setDatePreset(reportDatePresetByLabel["Last month"]);
        setIssueDateFrom(null)
        setIssueDateTo(null)
        setSelectedClient("");
        setSitesByClient(sites)
        setSelectedSiteByClient("")
        setPcnsGroupBy(0)
        setSelectedTags([])
        const zeroRevenueSitesPreference = stringHelpers.zeroRevenueSitesPreference(zeroRevenueSitesPreferenceKey)
        setShowZeroRevenueSites(zeroRevenueSitesPreference)
        setAppliedFilters({
            "Payment Date Range": `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`,
            "Group By": pcnsGroupByOptions[0]
        });
        if (zeroRevenueSitesPreference) {
            setAppliedFilters(prevState => ({ ...prevState, "Zero-Revenue Sites": "Included" }))
        }
        setShowFilterPills(true);
        fetchPCNPaidReports(defaultRange.firstDate, defaultRange.lastDate, "", "", 0, [], null, null);
    }

    const clearFilterByLabel = (label) => {
        switch (label) {
            case "Client":
                setSelectedClient("")
                setSelectedSiteByClient("")
                setSitesByClient(sites)
                fetchPCNPaidReports(fromDate, toDate, "", "", pcnsGroupBy, selectedTags, issueDateFrom, issueDateTo)
                break
            case "Site":
                setSelectedSiteByClient("")
                fetchPCNPaidReports(fromDate, toDate, "", selectedClient, pcnsGroupBy, selectedTags, issueDateFrom, issueDateTo)
                break
            case "Payment Date Range":
            case "Payment Date From":
            case "Payment Date To":
                if (isDateRangeComplete(issueDateFrom, issueDateTo)) {
                    setFromDate(null)
                    setToDate(null)
                    setDatePreset(null)
                    fetchPCNPaidReports(null, null, selectedSiteByClient, selectedClient, pcnsGroupBy, selectedTags, issueDateFrom, issueDateTo)
                } else {
                    const defaultRange = dateRange("lastMonth")
                    setFromDate(defaultRange.firstDate)
                    setToDate(defaultRange.lastDate)
                    setDatePreset(reportDatePresetByLabel["Last month"])
                    fetchPCNPaidReports(defaultRange.firstDate, defaultRange.lastDate, selectedSiteByClient, selectedClient, pcnsGroupBy, selectedTags, issueDateFrom, issueDateTo)
                }
                break
            case "Issue Date Range":
            case "Issue Date From":
            case "Issue Date To":
                setIssueDateFrom(null)
                setIssueDateTo(null)
                if (isDateRangeComplete(fromDate, toDate)) {
                    fetchPCNPaidReports(fromDate, toDate, selectedSiteByClient, selectedClient, pcnsGroupBy, selectedTags, null, null)
                } else {
                    const defaultRange = dateRange("lastMonth")
                    setFromDate(defaultRange.firstDate)
                    setToDate(defaultRange.lastDate)
                    setDatePreset(reportDatePresetByLabel["Last month"])
                    fetchPCNPaidReports(defaultRange.firstDate, defaultRange.lastDate, selectedSiteByClient, selectedClient, pcnsGroupBy, selectedTags, null, null)
                }
                break
            case "Group By":
                setPcnsGroupBy(0)
                fetchPCNPaidReports(fromDate, toDate, selectedSiteByClient, selectedClient, 0, selectedTags, issueDateFrom, issueDateTo)
                break
            case "Zero-Revenue Sites":
                setShowZeroRevenueSites(false)
                localStorage.setItem(zeroRevenueSitesPreferenceKey, JSON.stringify(false))
                parseRawData(reportsRawData, false, pcnsGroupBy)
                break
            case "Tags":
                setSelectedTags([])
                fetchPCNPaidReports(fromDate, toDate, selectedSiteByClient, selectedClient, pcnsGroupBy, [], issueDateFrom, issueDateTo)
                break
            default:
                break
        }
    }

    const handleFilterPillClose = closedLabel => {
        clearFilterByLabel(closedLabel)
        setAppliedFilters(prevState => {
            const updatedFilters = { ...prevState }
            const defaultRange = dateRange("lastMonth")
            const defaultPaymentDateRangeLabel = `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`
            if (closedLabel.startsWith("Payment Date")) {
                updatedFilters["Payment Date Range"] = isDateRangeComplete(issueDateFrom, issueDateTo) ? null : defaultPaymentDateRangeLabel
                updatedFilters["Payment Date From"] = null
                updatedFilters["Payment Date To"] = null
            } else if (closedLabel.startsWith("Issue Date")) {
                updatedFilters["Issue Date Range"] = null
                updatedFilters["Issue Date From"] = null
                updatedFilters["Issue Date To"] = null
                if (!isDateRangeComplete(fromDate, toDate)) {
                    updatedFilters["Payment Date Range"] = defaultPaymentDateRangeLabel
                    updatedFilters["Payment Date From"] = null
                    updatedFilters["Payment Date To"] = null
                }
            } else if (closedLabel === "Client") {
                updatedFilters.Client = null
                updatedFilters.Site = null
            } else if (closedLabel === "Group By") {
                updatedFilters["Group By"] = pcnsGroupByOptions[0]
            } else {
                updatedFilters[closedLabel] = null
            }
            return appliedFiltersUtilities.hasRemainingFilters(updatedFilters) ? updatedFilters : null
        })
    }

    const handleFilterPillItemRemove = (label, remainingIds) => {
        setAppliedFilters(prevState => appliedFiltersUtilities.keepMultiSelectionItems(prevState, label, remainingIds))
        removeMultiSelectFilterItemByLabel(label, remainingIds)
    }

    const removeMultiSelectFilterItemByLabel = (label, remainingIds) => {
        switch (label) {
            case "Tags":
                setSelectedTags(remainingIds)
                fetchPCNPaidReports(fromDate, toDate, selectedSiteByClient, selectedClient, pcnsGroupBy, remainingIds, issueDateFrom, issueDateTo)
                break
            default:
                break
        }
    }

    const parseRawData = (rawData, isZeroRevenueEnabled, groupBy) => {
        let temp
        if (groupBy === 0) {
            let dataFilteredByZeroRevenueToggle = !isZeroRevenueEnabled ? rawData.filter(dataItem => dataItem.amount * dataItem.count > 0) : rawData
            temp = dataFilteredByZeroRevenueToggle.map(item => (
                {
                    label: item.name,
                    siteId: item.siteId,
                    amount: stringHelpers.formatDecimal(item.amount, 2, "£"),
                    count: item.count,
                    subTotal: stringHelpers.formatDecimal((item.amount * item.count), 2, "£")
                }
            ))
            setPcnsPaidColumnTotal(dataFilteredByZeroRevenueToggle.reduce((acc, curr) => acc += curr.count, 0))
            setTotalCount(stringHelpers.formatDecimal(dataFilteredByZeroRevenueToggle.reduce((acc, curr) => acc += (curr.amount * curr.count), 0), 2, "£"))
        } else {
            let dataFilteredByZeroRevenueToggle = !isZeroRevenueEnabled ? rawData.filter(dataItem => dataItem.totalAmount > 0) : rawData
            temp = dataFilteredByZeroRevenueToggle.map(item => (
                {
                    // Site rows carry `name`; Tag and Tag-and-Site rows carry `tagName` (Tag-and-Site also `siteName`)
                    label: groupBy === 1 ? item.name : item.tagName,
                    siteId: item.siteId,
                    siteName: item.siteName,
                    count: item.count,
                    totalAmount: item.totalAmount,
                }
            ));
            setPcnsPaidColumnTotal(dataFilteredByZeroRevenueToggle.reduce((acc, curr) => acc += curr.count, 0))
            setTotalCount(stringHelpers.formatDecimal(dataFilteredByZeroRevenueToggle.reduce((acc, curr) => acc += curr.totalAmount, 0), 2, "£"))
        }
        setReportsData(temp);
    }

    const fetchPCNPaidReports = (paymentFrom, paymentTo, siteId, clientId, groupBy, tagIds, issueFrom, issueTo) => {
        setIsLoading(true)
        Api.getPcnsPaidReports(
            reportData => {
                setReportsRawData(reportData)
                parseRawData(reportData, stringHelpers.zeroRevenueSitesPreference(zeroRevenueSitesPreferenceKey), groupBy)
                setIsLoading(false);
            },
            () => { setIsLoading(false) },
            paymentFrom, paymentTo, issueFrom, issueTo, 0, !stringHelpers.isNullOrEmpty(siteId) ? siteId : null,
            !stringHelpers.isNullOrEmpty(clientId) ? clientId : null, groupBy, tagIds, organisationId, token
        )
    }

    const handleDownload = () => {
        Api.getPcnsPaidReports(
            (blob, filename) => { saveAs(blob, filename) },
            () => { setIsLoading(false) },
            fromDate,
            toDate,
            issueDateFrom,
            issueDateTo,
            1,
            !stringHelpers.isNullOrEmpty(selectedSiteByClient) ? selectedSiteByClient : null,
            !stringHelpers.isNullOrEmpty(selectedClient) ? selectedClient : null,
            pcnsGroupBy,
            selectedTags,
            organisationId,
            token
        )
    }


    const scheduleDateParameters = datePreset !== null
        ? { reportDatePreset: datePreset }
        : { from: fromDate, to: toDate }

    const scheduleInitialParameters = {
        ...scheduleDateParameters,
        issueDateFrom: issueDateFrom,
        issueDateTo: issueDateTo,
        siteId: selectedSiteByClient,
        clientId: selectedClient,
        pcnPaidGroupBy: pcnsGroupBy,
        tagIds: selectedTags,
    }

    const pcnPaymentsLink = (siteId, tagIds) => ({
        pathname: `/organisation/${ organisationId }/raw-data/pcn-payments`,
        state: {
            paymentDateFrom: fromDate,
            paymentDateTo: toDate,
            issueDateFrom: issueDateFrom,
            issueDateTo: issueDateTo,
            clientId: selectedClient,
            siteId: siteId ?? selectedSiteByClient,
            tagIds: tagIds ?? selectedTags,
        }
    })

    const tagIdByName = tagName => tags?.find(tag => tag.name === tagName)?.id

    const renderSiteLink = (siteId, siteName) => !stringHelpers.isNullOrEmpty(siteId)
        ? <Link to={ pcnPaymentsLink(siteId) }>{ siteName }</Link>
        : siteName

    const renderRowLabel = item => {
        if (pcnsGroupBy === 0 || pcnsGroupBy === 1) {
            return renderSiteLink(item.siteId, item.label)
        }
        const tagId = pcnsGroupBy === 2 ? tagIdByName(item.label) : null
        return !stringHelpers.isNullOrEmpty(tagId)
            ? <Link to={ pcnPaymentsLink(null, [tagId]) }>{ item.label }</Link>
            : item.label
    }

    const closeScheduleExportModal = () => setShowScheduleExportModal(false)
    useEffect(() => {
        fetchPCNPaidReports(fromDate, toDate, selectedSiteByClient, selectedClient, pcnsGroupBy, selectedTags, issueDateFrom, issueDateTo);
        setAppliedFilters({
            "Payment Date Range": `${ toDateString(new Date(fromDate)) } - ${ toDateString(new Date(toDate)) }`,
            "Group By": pcnsGroupByOptions[pcnsGroupBy]
        });
        if (showZeroRevenueSites) {
            setAppliedFilters(prevState => ({ ...prevState, "Zero-Revenue Sites": "Included" }))
        }
        setShowFilterPills(true);

    }, [])

    useEffect(() => {
        if (!arrayUtilities.isNullOrEmpty(reportsData)) {
            if (pcnsGroupBy === 0) {
                const amountOptions = reportsData.reduce((acc, curr) => {
                    if (!acc.includes(curr.amount)) acc.push(curr.amount);
                    return acc;
                }, [])

                const countsBySiteName = reportsData.reduce((acc, curr) => {
                    acc[curr.label] = {
                        ...acc[curr.label],
                        [`${ curr.amount }`]: curr.count
                    }
                    return acc
                }, {})
                setChartData(createGroupedChartTraces(amountOptions, countsBySiteName));
            } else {
                // summed per label so Tag and Site rows chart as one bar per tag
                const totalAmountByLabel = reportsData.reduce((acc, curr) => {
                    acc[curr.label] = (acc[curr.label] ?? 0) + Number(curr.totalAmount)
                    return acc
                }, {})
                setChartData(Object.entries(totalAmountByLabel).map(([label, value]) => ({
                    "label": label,
                    "value": value,
                })));
            }
        }
    }, [reportsData])

    const isSiteAndAmountGrouping = pcnsGroupBy === 0
    const isTagAndSiteGrouping = pcnsGroupBy === 3
    const groupingColumnHeading = pcnsGroupBy === 1 ? "Site Name" : "Tag"
    const chartTooltipValueLabel = isSiteAndAmountGrouping ? "" : "Amount"
    const chartTooltipValuePrefix = isSiteAndAmountGrouping ? "" : "£"
    const selectedTagNames = !arrayUtilities.isNullOrEmpty(selectedTags) && !arrayUtilities.isNullOrEmpty(tags)
        ? selectedTags
            .map(selectedTagId => tags.find(tag => tag.id === selectedTagId))
            .filter(tag => tag !== undefined)
            .map(tag => tag.name)
            .join(", ")
        : "All"

    return (
        <>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h5 className="mb-0">
                                            PCN Revenue
                                        </h5>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div className="d-flex mr-2 align-items-center">
                                            <FiltersModalTrigger onClick={ () => setShowFiltersModal(true) } />
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <button
                                                onClick={ () => handleDownload() }
                                                className="btn btn-sm btn-default text-white bg-dark nowrap mr-2"
                                            >
                                                Download CSV<i className="ml-2 fas fa-file-excel"></i>
                                            </button>
                                            <button
                                                onClick={ () => downloadReportPdf(pdfRef, `${ organisationName } - PCN Revenue`) }
                                                className="btn btn-sm btn-default nowrap mr-2 text-white" style={ { backgroundColor: "#4A7C8E" } }
                                            >
                                                Download PDF<i className="ml-2 far fa-file-pdf"></i>
                                            </button>
                                            <button
                                                onClick={ () => setShowScheduleExportModal(true) }
                                                className="btn btn-sm btn-secondary nowrap mr-2"
                                            >
                                                Schedule Report<i className="ml-2 far fa-clock"></i>
                                            </button>
                                            <Link
                                                className="btn btn-sm btn-primary nowrap mr-2"
                                                to={ pcnPaymentsLink() }>
                                                View Payments<i className="ml-2 fas fa-list"></i>
                                            </Link>
                                            <Link
                                                className="btn btn-falcon-default btn-sm mr-3"
                                                type="button"
                                                to={ reportsLandingUrl }>
                                                <i className="fas fa-reply"></i> Back
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body border-top d-flex flex-column">
                                {
                                    showFilterPills && appliedFilters &&
                                    <div className="d-flex align-items-center flex-wrap">
                                        {
                                            Object.entries(appliedFilters).map(
                                                ([label, value]) => {
                                                    const isResettable = (label.startsWith("Payment Date") && !isDateRangeComplete(issueDateFrom, issueDateTo)) || label === "Group By"
                                                    return <AppliedFilterPill
                                                        key={ label }
                                                        label={ label }
                                                        value={ value }
                                                        isResettable={ isResettable }
                                                        renderItemLabel={ renderTagFilterItemLabel }
                                                        onTextClick={ () => setShowFiltersModal(true) }
                                                        onCloseClick={ handleFilterPillClose }
                                                        onRemoveItem={ handleFilterPillItemRemove }
                                                    />
                                                }
                                            )
                                        }
                                        { appliedFilters && Object.keys(appliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={ () => handleResetFilters() }>Reset all filters</button> }
                                    </div>
                                }
                                <div ref={ pdfRef }>
                                    <PrintPdfHeader organisationLogo={ organisationLogo } organisationName={ organisationName } title="PCN Revenue">
                                        {
                                            (fromDate || toDate) &&
                                            <div className="print-only-filter">
                                                <span className="print-only-filter-label">Payment Date Range</span>
                                                <span className="print-only-filter-value">
                                                    { toDateString(fromDate) } - { toDateString(toDate) }
                                                </span>
                                            </div>
                                        }
                                        {
                                            (issueDateFrom || issueDateTo) &&
                                            <div className="print-only-filter">
                                                <span className="print-only-filter-label">Issue Date Range</span>
                                                <span className="print-only-filter-value">
                                                    { toDateString(issueDateFrom) } - { toDateString(issueDateTo) }
                                                </span>
                                            </div>
                                        }
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Client</span>
                                            <span className="print-only-filter-value">
                                                { pdfClientName(selectedClient) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Site</span>
                                            <span className="print-only-filter-value">
                                                { pdfSiteName(selectedSiteByClient) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Group By</span>
                                            <span className="print-only-filter-value">
                                                { pdfGroupByName(pcnsGroupBy) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Tags</span>
                                            <span className="print-only-filter-value">
                                                { selectedTagNames }
                                            </span>
                                        </div>
                                    </PrintPdfHeader>
                                {
                                    isLoading
                                        ? <Loader />
                                        : <>
                                            {
                                                !arrayUtilities.isNullOrEmpty(reportsData)
                                                    ? (
                                                        <div className="mt-3 d-flex flex-column">
                                                            {
                                                                chartData &&
                                                                <ReportsCharts tooltipCustomValueLabel={ chartTooltipValueLabel } tooltipValuePrefix={ chartTooltipValuePrefix } isGroupedChart={ isSiteAndAmountGrouping } data={ chartData } chartStyle="bar" showLegend={ isSiteAndAmountGrouping } />
                                                            }
                                                            <div className="card-body bg-white border-top p-0">
                                                                <table className="table table-sm mb-0 table-striped table-dashboard fs--1 data-table border-bottom border-200 no-footer ">
                                                                    <thead className="bg-200 text-900">
                                                                        {
                                                                            pcnsGroupBy === 0
                                                                                ? <tr>
                                                                                    <th>Site Name</th>
                                                                                    <th className="text-right">Amount</th>
                                                                                    <th className="text-right">PCNs Paid</th>
                                                                                    <th className="text-right">Subtotal</th>
                                                                                </tr>
                                                                                : <tr>
                                                                                    <th>{ groupingColumnHeading }</th>
                                                                                    { isTagAndSiteGrouping && <th>Site Name</th> }
                                                                                    <th className="text-right">PCNs Paid</th>
                                                                                    <th className="text-right">Amount</th>
                                                                                </tr>
                                                                        }
                                                                    </thead>
                                                                    <tbody>
                                                                        {
                                                                            pcnsGroupBy === 0
                                                                                ? reportsData.map(
                                                                                    (item, index) => <tr className={ item.subTotal === "£0.00" && "text-danger" } key={ index }>
                                                                                        <td>{ renderRowLabel(item) }</td>
                                                                                        <td className="text-right">{ item.amount }</td>
                                                                                        <td className="text-right">{ item.count }</td>
                                                                                        <td className="text-right">{ item.subTotal }</td>
                                                                                    </tr>
                                                                                )
                                                                                : reportsData.map(
                                                                                    (item, index) => <tr key={ index } className={ item.totalAmount === 0 && "text-danger" }>
                                                                                        <td>{ renderRowLabel(item) }</td>
                                                                                        { isTagAndSiteGrouping && <td>{ renderSiteLink(item.siteId, item.siteName) }</td> }
                                                                                        <td className="text-right">{ item.count }</td>
                                                                                        <td className="text-right">{ stringHelpers.formatDecimal(item.totalAmount, 2, "£") }</td>
                                                                                    </tr>
                                                                                )
                                                                        }
                                                                        {
                                                                            pcnsGroupBy === 0
                                                                                ? <tr className="font-weight-bold w-100">
                                                                                    <td>Total</td>
                                                                                    <td>&nbsp;</td>
                                                                                    <td className="text-right">{ pcnsPaidColumnTotal }</td>
                                                                                    <td className="text-right">
                                                                                        { totalCount }
                                                                                    </td>
                                                                                </tr>
                                                                                : <tr className="font-weight-bold w-100">
                                                                                    <td>Total</td>
                                                                                    { isTagAndSiteGrouping && <td>&nbsp;</td> }
                                                                                    <td className="text-right">{ pcnsPaidColumnTotal }</td>
                                                                                    <td className="text-right">
                                                                                        { totalCount }
                                                                                    </td>
                                                                                </tr>
                                                                        }
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    ) : <EmptyState message="No Data available for the selected filters" />
                                            }
                                        </>

                                }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {
                showFiltersModal &&
                <PcnsPaidReportFiltersModal
                    showModal={ showFiltersModal }
                    handleClose={ () => setShowFiltersModal(false) }
                    handleConfirm={ (draftFilters) => {
                        setFromDate(draftFilters.fromDate)
                        setToDate(draftFilters.toDate)
                        setDatePreset(draftFilters.datePreset)
                        setIssueDateFrom(draftFilters.issueDateFrom)
                        setIssueDateTo(draftFilters.issueDateTo)
                        setSelectedClient(draftFilters.selectedClient)
                        setSitesByClient(draftFilters.sitesByClient)
                        setSelectedSiteByClient(draftFilters.selectedSiteByClient)
                        setPcnsGroupBy(draftFilters.pcnsGroupBy)
                        setShowZeroRevenueSites(draftFilters.showZeroRevenueSites)
                        setSelectedTags(draftFilters.selectedTags)
                        localStorage.setItem(zeroRevenueSitesPreferenceKey, JSON.stringify(draftFilters.showZeroRevenueSites))
                        setAppliedFilters(draftFilters.appliedFilters)
                        setShowFilterPills(true)
                        setShowFiltersModal(false)
                        fetchPCNPaidReports(draftFilters.fromDate, draftFilters.toDate, draftFilters.selectedSiteByClient, draftFilters.selectedClient, draftFilters.pcnsGroupBy, draftFilters.selectedTags, draftFilters.issueDateFrom, draftFilters.issueDateTo)
                    } }
                    initialFromDate={ fromDate }
                    initialToDate={ toDate }
                    initialDatePreset={ datePreset }
                    initialIssueDateFrom={ issueDateFrom }
                    initialIssueDateTo={ issueDateTo }
                    initialSelectedClient={ selectedClient }
                    initialSelectedSiteByClient={ selectedSiteByClient }
                    initialSitesByClient={ sitesByClient }
                    initialPcnsGroupBy={ pcnsGroupBy }
                    initialShowZeroRevenueSites={ showZeroRevenueSites }
                    initialSelectedTags={ selectedTags }
                    zeroRevenueSitesPreferenceKey={ zeroRevenueSitesPreferenceKey }
                    initialAppliedFilters={ appliedFilters }
                    clients={ clients }
                    sites={ sites }
                    tags={ tags || [] }
                    organisationId={ organisationId }
                    token={ token }
                    key="pcns-paid-report-filters-modal"
                />
            }
            {
                showScheduleExportModal &&
                <PcnsPaidScheduleModal
                    sites={ sites }
                    clients={ clients }
                    tags={ tags || [] }
                    organisationId={ organisationId }
                    token={ token }
                    initialParameters={ scheduleInitialParameters }
                    initialSitesByClient={ sitesByClient }
                    onCancel={ closeScheduleExportModal }
                    onSaved={ closeScheduleExportModal }
                />
            }
        </>
    )
}
